/**
 * Verdictia · Product Feedback — Raisons & placeholders contextualisés
 *
 * Pure module (pas de dépendance React) : facile à tester, à étendre.
 * Le payload back reste inchangé : on sérialise plusieurs raisons en string
 * via " · " dans le champ `reason` (côté FeedbackDrawer).
 *
 * Clés supportées :
 *   - featureArea : "ai_comment" | "comment_details" | "summary" |
 *                   "contract_profile" | "metadata" | "analysis_stream" |
 *                   "export" | "global_review" (fallback)
 *   - sentiment   : "positive" | "negative"
 *   - commentType : pour "ai_comment" → "comparison" | "risk" | autre
 */

const REASONS = {
    ai_comment: {
        comparison: {
            positive: [
                "Changement bien détecté",
                "Diff référence/reçu juste",
                "Problème bien identifié",
                "Impact réaliste",
                "Recommandation actionnable",
                "Gain de temps",
            ],
            negative: [
                "Changement non détecté",
                "Diff référence/reçu erronée",
                "Problème mal compris",
                "Impact exagéré ou minimisé",
                "Recommandation peu actionnable",
                "Faux positif",
            ],
        },
        risk: {
            positive: [
                "Risque pertinent",
                "Niveau de risque bien calibré",
                "Description claire",
                "Action concrète",
                "Gain de temps",
            ],
            negative: [
                "Risque hors-sujet",
                "Risque sur-évalué",
                "Risque sous-évalué",
                "Description confuse",
                "Action peu utile",
                "Faux positif",
            ],
        },
        // fallback ai_comment (type inconnu)
        default: {
            positive: [
                "Pertinent",
                "Bien argumenté",
                "Action concrète",
                "Gain de temps",
            ],
            negative: [
                "Hors-sujet",
                "Imprécis / faux",
                "Trop vague",
                "Déjà évident",
                "Faux positif",
            ],
        },
    },
    comment_details: {
        default: {
            positive: [
                "Lecture stratégique pertinente",
                "Mécanisme contractuel bien expliqué",
                "Impact opérationnel concret",
                "Alignement marché juste",
                "Risque de litige réaliste",
                "Exemple de formulation utile",
                "Orientation claire et justifiée",
                "Justification solide",
            ],
            negative: [
                "Lecture stratégique faible",
                "Mécanisme contractuel imprécis",
                "Impact mal évalué",
                "Alignement marché douteux",
                "Risque de litige exagéré",
                "Exemple de formulation à revoir",
                "Orientation floue",
                "Source de référence manquante / fausse",
                "Citation du contrat erronée",
            ],
        },
    },
    summary: {
        default: {
            positive: ["Vue d'ensemble juste", "Bonne hiérarchisation", "Points clés couverts"],
            negative: ["Points clés manqués", "Trop générique", "Erreur factuelle", "Trop long"],
        },
    },
    contract_profile: {
        default: {
            positive: ["Profil pertinent", "Bien visualisé", "Classification juste"],
            negative: ["Lecture difficile", "Catégorie incorrecte", "Manque de détail"],
        },
    },
    metadata: {
        default: {
            positive: ["Données justes", "Champs complets"],
            negative: ["Champ manquant", "Valeur erronée", "Format incorrect"],
        },
    },
    analysis_stream: {
        default: {
            positive: ["Rapide", "Étapes claires"],
            negative: ["Trop lent", "Étape bloquée", "Message peu clair"],
        },
    },
    export: {
        default: {
            positive: ["Format propre", "Conforme attendu"],
            negative: ["Mise en page cassée", "Champs manquants", "Format inadapté"],
        },
    },
    global_review: {
        default: {
            positive: ["Interface fluide", "Gain de temps", "Résultat fiable"],
            negative: ["Lent", "Navigation confuse", "Bug d'interface", "Résultat décevant"],
        },
    },
};

const PLACEHOLDERS = {
    ai_comment: {
        comparison: {
            positive: "Qu'est-ce qui t'a aidé dans cette comparaison ?",
            negative: "Qu'est-ce qui cloche : le changement détecté, l'impact, la recommandation… ?",
        },
        risk: {
            positive: "Qu'est-ce qui rend ce risque utile à voir ?",
            negative: "Risque mal calibré ? Action peu utile ? Dis-nous.",
        },
        default: {
            positive: "Qu'est-ce qui t'a plu dans ce commentaire ?",
            negative: "Qu'est-ce qui rendrait ce commentaire plus juste ou plus utile ?",
        },
    },
    comment_details: {
        default: {
            positive: "Quelle section du mémo t'a le plus aidé ?",
            negative: "Quelle section du mémo est à revoir (lecture stratégique, alignement marché, rédaction cible…) ?",
        },
    },
    summary: {
        default: {
            positive: "Qu'est-ce qui t'a paru juste dans la synthèse ?",
            negative: "Qu'est-ce qui manque ou qui est faux dans la synthèse ?",
        },
    },
    contract_profile: {
        default: {
            positive: "Qu'est-ce qui rend ce profil utile ?",
            negative: "Qu'est-ce qui pourrait rendre ce profil plus lisible ou plus juste ?",
        },
    },
    metadata: {
        default: {
            positive: "Quelles données t'ont semblé fiables ?",
            negative: "Quel champ est manquant ou incorrect ?",
        },
    },
    analysis_stream: {
        default: {
            positive: "Qu'est-ce qui rend l'analyse fluide ?",
            negative: "Qu'est-ce qui ralentit ou bloque l'analyse ?",
        },
    },
    export: {
        default: {
            positive: "Qu'est-ce qui t'a paru propre dans l'export ?",
            negative: "Qu'est-ce qui ne va pas dans le format ou le contenu de l'export ?",
        },
    },
    global_review: {
        default: {
            positive: "Qu'est-ce qui t'a aidé dans ton travail ?",
            negative: "Qu'est-ce qui pourrait rendre l'expérience meilleure ?",
        },
    },
};

const FALLBACK_AREA = "global_review";

function pickByType(byType, commentType) {
    if (!byType) return null;
    if (commentType && byType[commentType]) return byType[commentType];
    return byType.default || null;
}

/**
 * @param {Object} params
 * @param {string} params.featureArea
 * @param {"positive"|"negative"} params.sentiment
 * @param {string|null} [params.commentType]
 * @returns {string[]}
 */
export function getReasons({ featureArea, sentiment, commentType = null } = {}) {
    if (!sentiment) return [];
    const area = REASONS[featureArea] || REASONS[FALLBACK_AREA];
    const bucket = pickByType(area, commentType);
    if (!bucket) return [];
    return bucket[sentiment] || [];
}

/**
 * @param {Object} params
 * @param {string} params.featureArea
 * @param {"positive"|"negative"|null} params.sentiment
 * @param {string|null} [params.commentType]
 * @returns {string}
 */
export function getPlaceholder({ featureArea, sentiment, commentType = null } = {}) {
    const area = PLACEHOLDERS[featureArea] || PLACEHOLDERS[FALLBACK_AREA];
    const bucket = pickByType(area, commentType);
    if (!sentiment || !bucket) {
        return "Donne-nous plus de contexte (optionnel)…";
    }
    return bucket[sentiment] || "Donne-nous plus de contexte (optionnel)…";
}

/** Sérialise plusieurs raisons en string compatible avec le champ back `reason`. */
export function serializeReasons(reasons) {
    if (!Array.isArray(reasons) || reasons.length === 0) return null;
    return reasons.join(" · ");
}

/** Multi-sélection autorisée pour les zones riches (ex. mémo). */
export function allowsMultiSelect(featureArea) {
    return featureArea === "comment_details";
}

